import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, HelpCircle, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { nextDayClause } from '../../data/toolsData';

const NextDayTool = () => {
  const navigate = useNavigate();
  const [currentDecision, setCurrentDecision] = useState('');
  const [answers, setAnswers] = useState({});
  const [showAnalysis, setShowAnalysis] = useState(false);

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const getVerdict = () => {
    const yesCount = Object.values(answers).filter(a => a === 'yes').length;
    const noCount = Object.values(answers).filter(a => a === 'no').length;
    
    if (yesCount >= 4) return { type: 'go', message: 'Esta decisão parece alinhada com as vossas prioridades. Avancem com confiança.' };
    if (noCount >= 3) return { type: 'stop', message: 'Talvez valha a pena reconsiderar. Esta decisão pode não estar alinhada com o que realmente importa.' };
    return { type: 'pause', message: 'Há sinais mistos. Deem-se mais 24 horas antes de decidir.' };
  };

  const answeredCount = Object.keys(answers).length;
  const verdict = answeredCount === 5 ? getVerdict() : null;

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/ferramentas')}
          className="flex items-center gap-2 text-[#8B7D6B] hover:text-[#6B4E37] mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Voltar às ferramentas
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-[#C5D1DC]/30 flex items-center justify-center mx-auto mb-6">
            <Clock size={32} className="text-[#6B4E37]" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {nextDayClause.title}
          </h1>
          <p className="text-lg text-[#6B4E37] max-w-xl mx-auto">
            {nextDayClause.subtitle}
          </p>
        </div>

        {/* Decision Input */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-sm">
          <label className="block text-sm font-medium text-[#8B7D6B] mb-3">
            Qual é a decisão que estão a considerar?
          </label>
          <textarea
            value={currentDecision}
            onChange={(e) => setCurrentDecision(e.target.value)}
            placeholder="Ex: Gastar mais €500 em centros de mesa, convidar o primo distante, mudar a data..."
            className="w-full p-4 bg-[#F5F3F0] border-0 rounded-lg text-[#2C2C2C] placeholder-[#8B7D6B] resize-none focus:ring-2 focus:ring-[#C4B5A0] outline-none"
            rows={3}
          />
        </div>

        {/* Questions */}
        {currentDecision && (
          <div className="space-y-4 mb-8 animate-fade-in">
            <h3 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-4">
              Respondam com honestidade:
            </h3>
            
            {nextDayClause.questions.map((q) => (
              <div 
                key={q.id}
                className="bg-white/80 backdrop-blur-sm rounded-xl p-5 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#C5D1DC]/30 flex items-center justify-center flex-shrink-0 mt-1">
                    <HelpCircle size={16} className="text-[#6B4E37]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[#2C2C2C] font-medium mb-2">{q.question}</p>
                    <p className="text-sm text-[#8B7D6B] mb-4">{q.context}</p>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleAnswer(q.id, 'yes')}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-300 ${
                          answers[q.id] === 'yes'
                            ? 'bg-[#B5C4B1] text-white'
                            : 'bg-[#F5F3F0] text-[#6B4E37] hover:bg-[#E8E4E0]'
                        }`}
                      >
                        Sim
                      </button>
                      <button
                        onClick={() => handleAnswer(q.id, 'no')}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-300 ${
                          answers[q.id] === 'no'
                            ? 'bg-[#E8D5D5] text-[#6B4E37]'
                            : 'bg-[#F5F3F0] text-[#6B4E37] hover:bg-[#E8E4E0]'
                        }`}
                      >
                        Não
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Verdict */}
        {verdict && (
          <div className={`rounded-2xl p-8 text-center animate-fade-in ${
            verdict.type === 'go' 
              ? 'bg-[#B5C4B1]/20 border border-[#B5C4B1]' 
              : verdict.type === 'stop'
              ? 'bg-[#E8D5D5]/20 border border-[#E8D5D5]'
              : 'bg-[#D4C4B0]/20 border border-[#D4C4B0]'
          }`}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-white/80">
              {verdict.type === 'go' && <CheckCircle size={32} className="text-[#B5C4B1]" />}
              {verdict.type === 'stop' && <XCircle size={32} className="text-[#E8D5D5]" />}
              {verdict.type === 'pause' && <AlertTriangle size={32} className="text-[#D4C4B0]" />}
            </div>
            <p className="text-lg text-[#2C2C2C] font-medium mb-2">
              {verdict.type === 'go' && 'Luz Verde'}
              {verdict.type === 'stop' && 'Reconsiderar'}
              {verdict.type === 'pause' && 'Pausa Recomendada'}
            </p>
            <p className="text-[#6B4E37]">{verdict.message}</p>
            
            <button
              onClick={() => {
                setCurrentDecision('');
                setAnswers({});
              }}
              className="mt-6 text-sm text-[#8B7D6B] hover:text-[#6B4E37] underline"
            >
              Avaliar outra decisão
            </button>
          </div>
        )}

        {/* Examples */}
        {!currentDecision && (
          <div className="mt-12">
            <h3 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-6 text-center">
              Exemplos práticos
            </h3>
            <div className="space-y-4">
              {nextDayClause.examples.map((example, index) => (
                <div 
                  key={index}
                  className="bg-white/60 backdrop-blur-sm rounded-xl p-5"
                >
                  <h4 className="font-medium text-[#2C2C2C] mb-2">{example.situation}</h4>
                  <p className="text-sm text-[#6B4E37] mb-3">{example.analysis}</p>
                  <span className={`text-xs px-3 py-1 rounded-full ${
                    example.verdict.includes('Manter') || example.verdict.includes('esperar')
                      ? 'bg-[#B5C4B1]/30'
                      : example.verdict.includes('Reconsiderar')
                      ? 'bg-[#D4C4B0]/30'
                      : 'bg-[#C5D1DC]/30'
                  } text-[#6B4E37]`}>
                    {example.verdict}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NextDayTool;
